import { Upload } from 'antd';
import { RcFile } from 'antd/es/upload/interface';
import React, { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import {
  AttachmentsAPI,
  FileAttachment,
  FileUploadStatus,
  getUpdatedFileList,
  UploadFileWithAttachment,
} from 'api/attachments';
import { StatusUpdateId } from 'api/models';
import { AttachmentGallery } from 'components/atoms/attachment-gallery';
import { toastService } from 'components/molecules/toast';
import { selectActiveStatusUpdate } from 'data/status-updates';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { getStatusUpdatesDetailQueryKey } from 'queries/status-updates';
import { handleDownload } from 'utils/attachment';
import styles from './attachments.module.css';
import { AddButton } from './helper-components';
import { AttachmentsEmpty, Description, NodeWrapper } from './list-styles';
import { AttachmentsTable } from './table';
import { AttachmentsTabViews } from './types';
import { convertFileAttachmentToUploadFileWithAttachment } from './utils';

const { Dragger } = Upload;

export const AttachmentsList: FC<{
  view?: AttachmentsTabViews;
  data?: FileAttachment[];
  readonly?: boolean;
  descriptionLabel?: React.ReactNode;
  onChange?(filesList: UploadFileWithAttachment[]): void;
  onUploadRequest(file: File): Promise<any>;
  onMount?: (ref) => void;
  externalTrigger?: (ref) => void;
  isBasicPreview?: boolean;
  externalTriggerEnabled?: boolean;
}> = ({
  view,
  data = [],
  readonly,
  descriptionLabel,
  onChange = () => {},
  onUploadRequest,
  onMount,
  externalTrigger,
  isBasicPreview,
  externalTriggerEnabled,
}) => {
  const [filesList, setFilesList] = useState<UploadFileWithAttachment[]>([]);
  const latestFileList = useRef<UploadFileWithAttachment[]>(filesList);
  const stringifyData = JSON.stringify(data);
  const uploadButtonRef = useRef<any>(null);
  const externalUploadRef = useRef<any>(null);
  const downloadAttachmentErrorText = useTranslation(
    'attachments.download.error',
  );
  const translate = useTranslate();
  const queryClient = useQueryClient();
  const activeStatusUpdate = useSelector(selectActiveStatusUpdate);

  useEffect(() => {
    setFilesList(
      convertFileAttachmentToUploadFileWithAttachment(
        JSON.parse(stringifyData),
      ),
    );
  }, [stringifyData]);

  useEffect(() => {
    onMount?.(uploadButtonRef);
  }, [uploadButtonRef, onMount]);

  useEffect(() => {
    externalTrigger?.(externalUploadRef);
  }, [uploadButtonRef, externalTrigger]);

  useEffect(() => {
    latestFileList.current = filesList;
  }, [filesList]);

  const updateFile = ({
    attachment,
    file,
    status,
  }: {
    attachment?: FileAttachment;
    file: RcFile;
    status: FileUploadStatus;
  }) => {
    const latestFiles = latestFileList.current;
    const fileToUpdate = latestFiles.find((f) => f.uid === file.uid);

    if (!fileToUpdate) {
      return;
    }

    const result = getUpdatedFileList(
      fileToUpdate,
      latestFiles,
      attachment,
      status,
    );

    if (result) {
      setFilesList(result);

      onChange(result);
    }
  };

  const customUploadRequest = (args) => {
    const { file } = args;

    onUploadRequest(file)
      .then((res) => {
        updateFile({
          attachment: res,
          file,
          status: FileUploadStatus.Done,
        });
      })
      .catch(({ response = {} }) => {
        const errorData = response.data || {};
        const [error = {}] = errorData.errors || [];

        if (error.code === 'ATTACHMENT_SIZE_EXCEEDED') {
          toastService({
            title: translate('attachment.error.size.title'),
            message: translate('attachment.add.description'),
          }).error();
        }
        updateFile({
          attachment: undefined,
          file,
          status: FileUploadStatus.Error,
        });
      });
  };

  const handleOnRemove = (file: UploadFileWithAttachment) => {
    const index = filesList.indexOf(file);
    const newFileList = [...filesList];

    newFileList.splice(index, 1);

    setFilesList(newFileList);
    onChange(newFileList);

    if (file.attachment?.attachmentId) {
      AttachmentsAPI.deleteAttachment(file.attachment?.attachmentId).then(
        () => {
          if (activeStatusUpdate) {
            queryClient.refetchQueries(
              getStatusUpdatesDetailQueryKey(
                StatusUpdateId(activeStatusUpdate.statusUpdateId),
              ),
            );
          }
        },
      );
    }

    return true;
  };

  let node;

  switch (view) {
    case AttachmentsTabViews.GridView:
      node = (
        <AttachmentGallery
          large
          isBasicPreview={isBasicPreview}
          className={styles.gallery}
          filesList={filesList}
          onRemoveFile={handleOnRemove}
          onDownloadFile={(file) =>
            handleDownload(
              file.attachment?.attachmentId,
              downloadAttachmentErrorText,
            )
          }
        />
      );
      break;
    case AttachmentsTabViews.TableView:
    default:
      node = (
        <AttachmentsTable
          filesList={filesList}
          onRemoveFile={handleOnRemove}
          onDownloadFile={(file) =>
            handleDownload(
              file.attachment?.attachmentId,
              downloadAttachmentErrorText,
            )
          }
        />
      );
  }

  const handleOnChange = (args) => {
    const { file } = args;

    setFilesList([...latestFileList.current, file]);
  };

  return (
    <>
      <Dragger
        disabled={readonly}
        className={styles.dragger}
        customRequest={customUploadRequest}
        onChange={handleOnChange}
        multiple
        showUploadList={false}
        openFileDialogOnClick={!filesList.length}
      >
        <div className={styles.content_wrapper} ref={uploadButtonRef}>
          {filesList.length ? (
            <NodeWrapper readonly={readonly}>{node}</NodeWrapper>
          ) : (
            <AttachmentsEmpty>
              {/* Note:: Add button is dummy here, if user clicks on it,
              the event will propagate to dragger and open file manager. */}
              {!readonly ? (
                <>
                  <AddButton />
                  <Description>
                    {descriptionLabel ?? (
                      <FormattedMessage id="attachment.add.description" />
                    )}
                  </Description>
                </>
              ) : (
                <Description>
                  <FormattedMessage id="attachment.not_found" />
                </Description>
              )}
            </AttachmentsEmpty>
          )}
        </div>
      </Dragger>
      {(externalTriggerEnabled || filesList.length > 0) && !readonly && (
        <Upload
          customRequest={customUploadRequest}
          onChange={handleOnChange}
          multiple
          showUploadList={false}
        >
          {externalTriggerEnabled ? (
            <div ref={externalUploadRef} />
          ) : (
            <AddButton />
          )}
        </Upload>
      )}
    </>
  );
};
